import React, { useState, useEffect, useCallback } from 'react';
import { PeriodSelector } from '../../../components/common/PeriodSelector';
import { Button } from '../../../components/ui/button';
import { TabsContent } from '../../../components/ui/tabs';
import { pagosApi, resultadosApi } from '../../../lib/api';
import { useViewportFillHeight } from '../../../hooks/useViewportFillHeight';
import { ChevronLeft, ChevronRight, Upload, Download } from 'lucide-react';

const estadoStyles = {
  // Pago al propietario
  falta_factura: 'bg-amber-50 text-amber-700 border-amber-300',
  pendiente: 'bg-blue-50 text-blue-700 border-blue-300',
  pagado: 'bg-green-50 text-green-700 border-green-300',
  // Cobro al propietario
  pendiente_cobro: 'bg-orange-50 text-orange-700 border-orange-300',
  cobrado: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  // Sin movimiento
  sin_movimiento: 'bg-slate-50 text-slate-500 border-slate-200',
};

const TIPO_LABEL = {
  pago: { text: 'Pago al propietario', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  cobro: { text: 'Cobro al propietario', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  sin_movimiento: { text: 'Sin movimiento', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

// ─── Sub-componentes (extraídos para evitar remontaje en cada render del padre) ───

// Estado DERIVADO en backend: comprobante → Pagado/Cobrado; sin factura → Falta
// factura; con factura → Pago pendiente. Solo lectura (ya no se edita a mano).
const ESTADO_LABEL = {
  falta_factura: 'Falta factura',
  pendiente: 'Pago pendiente',
  pagado: 'Pagado',
  pendiente_cobro: 'Pendiente de cobro',
  cobrado: 'Cobrado',
  sin_movimiento: 'Sin movimiento',
};

const EstadoBadgeAuto = ({ cn, estado, testId }) => (
  <span
    className={cn(
      "inline-block text-xs font-medium rounded-full border px-2 py-0.5 whitespace-nowrap",
      estadoStyles[estado] || estadoStyles.sin_movimiento
    )}
    data-testid={testId}
  >
    {ESTADO_LABEL[estado] || estado}
  </span>
);

const ComprobanteCellInner = ({ tipo, tiene_comprobante, unidadId, propietarioId, edificioId, selectedPeriodo, uploadingComp, onUpload }) => {
  if (tipo === 'sin_movimiento') return <span className="text-xs text-slate-400">-</span>;
  const key = `${unidadId}-${propietarioId}`;
  const inputId = `comp-${key}`;
  return (
    <div className="flex flex-col items-center gap-1">
      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" id={inputId}
        onChange={(e) => { if (e.target.files?.[0]) onUpload(unidadId, propietarioId, e.target.files[0]); }} />
      {tiene_comprobante && (
        <a href={pagosApi.downloadComprobanteUrl(edificioId, unidadId, selectedPeriodo, propietarioId)}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-green-600 font-medium hover:text-green-800"
          data-testid={`pago-comprobante-ver-${key}`}>
          <Download className="w-3.5 h-3.5" /> Ver
        </a>
      )}
      <Button size="sm"
        variant={tiene_comprobante ? 'outline' : 'default'}
        className={tiene_comprobante ? 'h-5 text-[9px] px-1.5' : 'h-6 text-[10px] px-1.5 bg-[#1B4D3E] hover:bg-[#163d32]'}
        disabled={uploadingComp === key}
        data-testid={`pago-comprobante-subir-${key}`}
        onClick={() => document.getElementById(inputId)?.click()}>
        {uploadingComp === key ? '...' : (tiene_comprobante ? 'Reemplazar' : <><Upload className="w-3 h-3 mr-0.5" />Subir</>)}
      </Button>
    </div>
  );
};

const FacturaPropCellInner = ({ tipo, tiene_factura, resultadoId, propietarioId, uploadingFact, onUpload }) => {
  if (tipo === 'sin_movimiento' || !resultadoId) return <span className="text-xs text-slate-400">—</span>;
  const key = `${resultadoId}-${propietarioId}`;
  const inputId = `fact-prop-${key}`;
  return (
    <div className="flex flex-col items-center gap-1">
      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" id={inputId}
        onChange={(e) => { if (e.target.files?.[0]) onUpload(resultadoId, propietarioId, e.target.files[0]); }} />
      {tiene_factura && (
        <a href={resultadosApi.downloadFacturaPropietario(resultadoId, propietarioId)}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-green-600 font-medium hover:text-green-800"
          data-testid={`pago-factura-prop-ver-${key}`}>
          <Download className="w-3.5 h-3.5" /> Ver
        </a>
      )}
      <Button size="sm"
        variant={tiene_factura ? 'outline' : 'default'}
        className={tiene_factura ? 'h-5 text-[9px] px-1.5' : 'h-6 text-[10px] px-1.5 bg-[#1B4D3E] hover:bg-[#163d32]'}
        disabled={uploadingFact === key}
        data-testid={`pago-factura-prop-subir-${key}`}
        onClick={() => document.getElementById(inputId)?.click()}>
        {uploadingFact === key ? '...' : (tiene_factura ? 'Reemplazar' : <><Upload className="w-3 h-3 mr-0.5" />Subir</>)}
      </Button>
    </div>
  );
};

const FechaInputInner = ({ value, onChange, disabled, testId }) => {
  const isoToRaw = (iso) => {
    if (!iso) return '';
    const p = iso.split('-');
    return p.length === 3 ? `${p[2]}${p[1]}${p[0]}` : '';
  };
  const [raw, setRaw] = useState(isoToRaw(value));
  useEffect(() => { setRaw(isoToRaw(value)); }, [value]);

  const formatDisplay = (r) => {
    if (!r) return '';
    if (r.length <= 2) return r;
    if (r.length <= 4) return `${r.slice(0, 2)}/${r.slice(2)}`;
    return `${r.slice(0, 2)}/${r.slice(2, 4)}/${r.slice(4)}`;
  };

  const handleSave = () => {
    if (raw.length === 8) {
      const d = raw.slice(0, 2), m = raw.slice(2, 4), y = raw.slice(4, 8);
      const dt = new Date(`${y}-${m}-${d}`);
      if (!isNaN(dt.getTime())) {
        const iso = `${y}-${m}-${d}`;
        if (iso !== value) onChange(iso);
        return;
      }
    }
    if (raw === '' && value) onChange('');
  };

  if (disabled) return <span className="text-xs text-slate-400">-</span>;

  return (
    <input
      type="text"
      value={formatDisplay(raw)}
      onChange={(e) => setRaw(e.target.value.replace(/\D/g, '').slice(0, 8))}
      onBlur={() => setTimeout(() => handleSave(), 150)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); handleSave(); } }}
      placeholder="dd/mm/aaaa"
      className="text-sm text-slate-600 border border-slate-200 rounded px-2 py-1 w-[95px] text-center"
      data-testid={testId}
    />
  );
};

export const TabPagos = ({ cn, contratoBadgeByUnidad = {}, cocherasAsociadasMap = {}, edificio, edificioId, formatImporte, navigatePeriodo, selectedPeriodo, setSelectedPeriodo, sortUnidades, toast, unidades }) => {
  const [tableRef, tableHeight] = useViewportFillHeight();
  const esTorreEmilia = edificio?.modelo_administracion === 'gc_factura_y_cobra';
  const PISO1_ID = 'unit_piso1_te';

  const [pagosData, setPagosData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingComp, setUploadingComp] = useState(null);
  const [uploadingFact, setUploadingFact] = useState(null);

  const fetchPagos = useCallback(async () => {
    if (!edificioId || !selectedPeriodo) return;
    setLoading(true);
    try {
      const res = await pagosApi.get(edificioId, selectedPeriodo);
      setPagosData(res.data);
    } catch (err) {
      console.error('Error fetching pagos:', err);
      setPagosData(null);
    } finally {
      setLoading(false);
    }
  }, [edificioId, selectedPeriodo]);

  useEffect(() => { fetchPagos(); }, [fetchPagos]);

  const handleFechaChange = async (unidadId, propietarioId, fecha, estadoActual) => {
    try {
      // El estado ya no se edita a mano: se manda el actual (derivado) y solo cambia la fecha.
      await pagosApi.update(edificioId, unidadId, selectedPeriodo, estadoActual, propietarioId, fecha);
      fetchPagos();
    } catch {
      toast.error('Error al actualizar fecha');
    }
  };

  const handleFacturaPropAdmin = async (resultadoId, propietarioId, file) => {
    const key = `${resultadoId}-${propietarioId}`;
    setUploadingFact(key);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await resultadosApi.uploadFacturaPropietarioAdmin(resultadoId, formData, propietarioId);
      toast.success('Factura del propietario cargada');
      fetchPagos();
    } catch {
      toast.error('Error al subir la factura del propietario');
    } finally {
      setUploadingFact(null);
    }
  };

  const handleComprobante = async (unidadId, propietarioId, file) => {
    const key = `${unidadId}-${propietarioId}`;
    setUploadingComp(key);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await pagosApi.uploadComprobante(edificioId, unidadId, selectedPeriodo, propietarioId, formData);
      toast.success('Comprobante subido');
      fetchPagos();
    } catch {
      toast.error('Error al subir comprobante');
    } finally {
      setUploadingComp(null);
    }
  };

  const itemsMap = {};
  if (pagosData?.items) {
    pagosData.items.forEach(i => { itemsMap[i.unidad_id] = i; });
  }

  // ─── Universo de unidades a mostrar (alineado con Liquid) ───
  const piso1MiembrosIds = esTorreEmilia
    ? new Set(unidades.filter(u => u.conjunto_alquilable_id === 'conj_piso1_te' && u.unidad_id !== PISO1_ID).map(u => u.unidad_id))
    : new Set();

  const unidadesEnAdmin = esTorreEmilia
    ? unidades.filter(u => {
        if (u.unidad_id === PISO1_ID) return true;
        if (u.en_administracion === false) return false;
        if (u.rol_unidad === 'componente') return false;
        if (piso1MiembrosIds.has(u.unidad_id)) return false;
        if (cocherasAsociadasMap[u.unidad_id]) return false; // cocheras asociadas van dentro de su oficina madre
        return true;
      })
    : unidades.filter(u => u.en_administracion !== false && !cocherasAsociadasMap[u.unidad_id]);

  return (
    <TabsContent value="pagos" className="mt-0">
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriodo('prev')}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="w-[120px]">
              <PeriodSelector value={selectedPeriodo} onChange={setSelectedPeriodo} placeholder="Período" />
            </div>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriodo('next')}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div ref={tableRef} className="overflow-auto" style={{ height: tableHeight }}>
            <table className="w-full text-sm table-fixed min-w-[1100px] min-h-full [&_tfoot_td]:sticky [&_tfoot_td]:bottom-0 [&_tfoot_td]:z-10 [&_tfoot_td]:bg-slate-100">
              <colgroup>
                <col style={{ width: '10%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '12%' }} />
              </colgroup>
              <thead className="sticky top-0 z-20 shadow-[0_1px_0_0_rgb(226,232,240)] bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="py-2.5 px-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50">Unidad</th>
                  <th className="py-2.5 px-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50">Propietario</th>
                  <th className="py-2.5 px-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50">%</th>
                  <th className="py-2.5 px-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50">Resultado</th>
                  <th className="py-2.5 px-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50">Tipo</th>
                  <th className="py-2.5 px-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50">Factura propietario</th>
                  <th className="py-2.5 px-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50">Comprobante</th>
                  <th className="py-2.5 px-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50">Fecha</th>
                  <th className="py-2.5 px-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {unidadesEnAdmin.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-slate-500">
                      {loading ? 'Cargando...' : 'Sin unidades administradas para mostrar'}
                    </td>
                  </tr>
                ) : sortUnidades(unidadesEnAdmin).flatMap(unidad => {
                  const item = itemsMap[unidad.unidad_id];
                  const ganancia = item ? item.ganancia : 0;
                  const tipo = item?.tipo_movimiento || (Math.abs(ganancia) <= 1 ? 'sin_movimiento' : (ganancia > 0 ? 'pago' : 'cobro'));
                  const props = item?.propietarios || [];
                  // Una fila por propietario; si no hay propietarios → placeholder único.
                  const filas = props.length > 0 ? props : [{
                    propietario_id: '',
                    propietario_nombre: '—',
                    porcentaje: 100,
                    monto: ganancia,
                    estado: tipo === 'pago' ? 'falta_factura' : tipo === 'cobro' ? 'pendiente_cobro' : 'sin_movimiento',
                    fecha_pago: null,
                    tiene_factura: false,
                    tiene_comprobante: false,
                  }];
                  const isMulti = filas.length > 1;
                  const tipoBadge = TIPO_LABEL[tipo];

                  return filas.map((prop, idx) => {
                    const gananciaCls = tipo === 'cobro' ? 'text-red-600' : tipo === 'pago' ? 'text-green-700' : 'text-slate-500';
                    return (
                      <tr key={`${unidad.unidad_id}-${prop.propietario_id || idx}`} className="hover:bg-slate-50/60" data-testid={`pago-row-${unidad.unidad_id}-${prop.propietario_id || idx}`}>
                        {idx === 0 ? (
                          <td className="py-2 px-3 font-medium text-slate-900 whitespace-nowrap align-top" rowSpan={filas.length}>
                            {unidad.nombre_unidad}
                            {isMulti && <div className="text-[10px] text-slate-400 mt-0.5">{filas.length} propietarios</div>}
                            {contratoBadgeByUnidad[unidad.unidad_id] && !contratoBadgeByUnidad[unidad.unidad_id].es_principal && (
                              <div className="mt-0.5">
                                <span
                                  className="inline-block px-1.5 py-0.5 rounded text-[10px] ring-1 bg-violet-50 text-violet-700 ring-violet-200"
                                  title={contratoBadgeByUnidad[unidad.unidad_id].inquilino}
                                  data-testid={`badge-contrato-pagos-${unidad.unidad_id}`}
                                >
                                  {contratoBadgeByUnidad[unidad.unidad_id].inquilino}
                                </span>
                              </div>
                            )}
                          </td>
                        ) : null}
                        <td className="py-2 px-3 text-sm text-slate-700 truncate" title={prop.propietario_nombre}>
                          {prop.propietario_nombre || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="py-2 px-3 text-right text-xs text-slate-500 whitespace-nowrap">
                          {prop.porcentaje != null ? `${prop.porcentaje}%` : '—'}
                        </td>
                        <td className={cn("py-2 px-3 text-right font-mono text-sm whitespace-nowrap", gananciaCls)}>
                          $ {formatImporte(prop.monto != null ? prop.monto : ganancia, 0)}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className={cn("inline-block text-xs font-medium rounded-full border px-2 py-0.5 whitespace-nowrap", tipoBadge.cls)}>
                            {tipoBadge.text}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <FacturaPropCellInner
                            tipo={tipo}
                            tiene_factura={prop.tiene_factura}
                            resultadoId={item?.resultado_id}
                            propietarioId={prop.propietario_id}
                            uploadingFact={uploadingFact}
                            onUpload={handleFacturaPropAdmin}
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          <ComprobanteCellInner
                            tipo={tipo}
                            tiene_comprobante={prop.tiene_comprobante}
                            unidadId={unidad.unidad_id}
                            propietarioId={prop.propietario_id}
                            edificioId={edificioId}
                            selectedPeriodo={selectedPeriodo}
                            uploadingComp={uploadingComp}
                            onUpload={handleComprobante}
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          <FechaInputInner
                            value={prop.fecha_pago}
                            onChange={(f) => handleFechaChange(unidad.unidad_id, prop.propietario_id, f, prop.estado)}
                            disabled={tipo === 'sin_movimiento' || !item}
                            testId={`pago-fecha-${unidad.unidad_id}-${prop.propietario_id || idx}`}
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          <EstadoBadgeAuto
                            cn={cn}
                            estado={prop.estado}
                            testId={`pago-estado-${unidad.unidad_id}-${prop.propietario_id || idx}`}
                          />
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
              {pagosData?.items?.length > 0 && (
                <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                  <tr className="font-semibold">
                    <td className="py-3 px-3" colSpan={3}></td>
                    <td className="py-3 px-3 text-slate-700 text-xs uppercase text-right">Resultado total</td>
                    <td className={cn(
                      "py-3 px-3 text-right font-mono whitespace-nowrap",
                      pagosData.total_ganancia >= 0 ? 'text-green-700' : 'text-red-600'
                    )}>
                      $ {formatImporte(pagosData.total_ganancia, 0)}
                    </td>
                    <td colSpan={4} className="py-3 px-3"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </TabsContent>
  );
};
